import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, Users, AlertTriangle } from "lucide-react";

// The treatment floor: who is here, and whether there is room to put them.
//
// rx books a doctor 1:1 at a time slot. A PT clinic runs one therapist across
// several plinths, so CAPACITY - not the therapist's diary - is what actually
// limits how many patients can be treated in an hour.
export default function PhysioFloor() {
  const nav = useNavigate();
  const [avail, setAvail] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/physio/resources/availability"),
      api.get("/queue/today").catch(() => ({ data: [] })),
    ])
      .then(([a, q]) => {
        setAvail(a.data);
        setQueue(Array.isArray(q.data) ? q.data : q.data?.queue || []);
      })
      .catch((e) => toast.error(formatErr(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Loading the floor…</div>;

  const waiting = queue.filter((q) => q.status === "waiting");
  const inProgress = queue.filter((q) => q.status === "in_progress");
  const done = queue.filter((q) => q.status === "completed");
  const resources = avail?.resources || [];
  const full = resources.filter((r) => r.at_capacity).length;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="physio-floor">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
          Treatment floor
        </h1>
        <p className="text-sm text-slate-500">{avail?.date}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Waiting</div>
          <div className="mt-1 text-3xl font-extrabold tabular-nums">{waiting.length}</div>
        </Card>
        <Card className="p-4 bg-teal-50 border-teal-200">
          <div className="text-xs uppercase tracking-wide text-teal-700 font-semibold">In treatment</div>
          <div className="mt-1 text-3xl font-extrabold tabular-nums text-teal-800">{inProgress.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Finished today</div>
          <div className="mt-1 text-3xl font-extrabold tabular-nums">{done.length}</div>
        </Card>
        <Card className={`p-4 ${full ? "bg-amber-50 border-amber-200" : ""}`}>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">At capacity</div>
          <div className="mt-1 text-3xl font-extrabold tabular-nums">{full}<span className="text-base text-slate-400"> / {resources.length}</span></div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b">
            <div className="font-bold text-slate-900 flex items-center gap-2">
              <Activity className="h-4 w-4" />Equipment &amp; space
            </div>
            <div className="text-xs text-slate-500">
              {avail?.sessions_today} session(s) recorded today
              {avail?.unassigned_sessions ? ` · ${avail.unassigned_sessions} not assigned to a station` : ""}
            </div>
          </div>
          <div className="divide-y">
            {resources.map((r) => (
              <div key={r.id} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-800">{r.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{r.resource_type?.replace(/_/g, " ")}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-28 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${r.at_capacity ? "bg-amber-500" : "bg-teal-500"}`}
                      style={{ width: `${Math.min(100, (r.booked / Math.max(1, r.capacity)) * 100)}%` }}
                    />
                  </div>
                  <div className="tabular-nums text-sm font-semibold w-14 text-right">
                    {r.booked}/{r.capacity}
                  </div>
                  {r.at_capacity && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                </div>
              </div>
            ))}
            {resources.length === 0 && (
              <div className="px-4 py-6 text-sm text-slate-500">No stations configured.</div>
            )}
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b">
            <div className="font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-4 w-4" />Today&rsquo;s queue
            </div>
            <div className="text-xs text-slate-500">{queue.length} on the list</div>
          </div>
          <div className="divide-y max-h-[28rem] overflow-y-auto">
            {queue.map((q) => (
              <div key={q.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">
                    <span className="text-slate-400 tabular-nums mr-2">#{q.queue_number}</span>
                    {q.patient_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{q.notes || q.purpose}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                    q.status === "in_progress" ? "bg-teal-50 text-teal-700 border-teal-200"
                      : q.status === "completed" ? "bg-slate-50 text-slate-500 border-slate-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {q.status?.replace(/_/g, " ")}
                  </span>
                  {q.patient_id && (
                    <Button size="sm" variant="outline"
                            onClick={() => nav(`/physio/patients/${q.patient_id}`)}>Chart</Button>
                  )}
                </div>
              </div>
            ))}
            {queue.length === 0 && (
              <div className="px-4 py-6 text-sm text-slate-500">Nobody in the queue today.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
